// useMobileHomeRooms — read-path hook for the mobile Home surface.
//
// Reuses the same `subscribeRooms` snapshot every desktop surface reads
// (`useMatrix().rooms`) and the shared `sortByActivity` / spaces helpers,
// so the phone list never opens a new data-fetch path. The desktop
// `RoomList` derives its muted set inline; the mobile shell cannot edit
// that file, so the small projection is mirrored here behind one hook.

import { useMemo } from "react";

import { useMatrix, useMatrixRooms } from "~/context/MatrixContext";
import { getRoomNotificationLevel, type RoomSummary } from "~/lib/matrix";
import { sortByActivity } from "~/lib/room-utils";
import { deriveSpaceHierarchy, visibleRoomIdsForSpace } from "~/lib/spaces";
import { useOrgDmMembership } from "~/hooks/useOrgDmMembership";

export interface UseMobileHomeRoomsResult {
  /** Top-level spaces, name-sorted — the text-only filter row's chips. */
  spaces: RoomSummary[];
  /** Joined, non-archived, non-space rooms scoped to `activeSpaceId`
   *  (null = every space), newest-activity first. DM rows are gated by
   *  the selected org roster — see `dmStatus`. */
  rooms: RoomSummary[];
  /** Ids of rooms whose notification level is `mute`. */
  mutedRoomIds: Set<string>;
  /** For rows whose name another visible row shares: the org to show
   *  after the name ("#general · avundo"). */
  disambiguationByRoomId: Map<string, string>;
  /** Unread messages per org space across its non-DM rooms (DMs belong
   *  to no single org). Muted rooms do not count. */
  unreadBySpace: Map<string, number>;
  /** Honest DM-section state so the shell can show pending / error /
   *  empty copy instead of an empty or an all-org DM burst.
   *  `all` means no org is selected and every DM shows. */
  dmStatus: "all" | "pending" | "ready" | "empty" | "error";
  /** Retry a failed org roster / DM load. */
  retryDmRoster: () => void;
}

/**
 * @param activeSpaceId The selected filter-row space, or `null` for "All".
 */
export function useMobileHomeRooms(activeSpaceId: string | null): UseMobileHomeRoomsResult {
  const { client } = useMatrix();
  const allRooms = useMatrixRooms();

  const spaces = useMemo(() => {
    const { orgSpaces } = deriveSpaceHierarchy(allRooms);
    return [...orgSpaces].sort((a, b) => a.name.localeCompare(b.name));
  }, [allRooms]);

  // A filter chip can vanish (the user left the space) while it's still
  // selected. Scope to "All" rather than an id no chip can clear.
  const effectiveSpaceId =
    activeSpaceId !== null && spaces.some((s) => s.id === activeSpaceId) ? activeSpaceId : null;

  const {
    all: orgAll,
    orgRoster,
    dmEligibleByRoomId,
    dmPendingByRoomId,
    retry: retryDmRoster,
  } = useOrgDmMembership(effectiveSpaceId, allRooms);

  const rooms = useMemo(() => {
    const joined = allRooms.filter((room) => !room.isSpace && room.archived === null);
    // "All" bypasses roster work entirely — every DM stays visible.
    if (orgAll) return sortByActivity(joined);
    const visibleIds = visibleRoomIdsForSpace(allRooms, effectiveSpaceId);
    const out = joined.filter((room) => {
      if (!room.isDirect) return visibleIds.has(room.id);
      // A DM is admitted only once its own roster has resolved AND it is
      // eligible — a pending / errored roster excludes the row rather
      // than flashing the whole account's DMs.
      if (dmPendingByRoomId.get(room.id)) return false;
      return dmEligibleByRoomId.get(room.id) === true;
    });
    return sortByActivity(out);
  }, [allRooms, effectiveSpaceId, orgAll, dmEligibleByRoomId, dmPendingByRoomId]);

  const dmStatus = useMemo((): UseMobileHomeRoomsResult["dmStatus"] => {
    if (orgAll) return "all";
    if (orgRoster.status === "error") return "error";
    if (orgRoster.status === "pending") return "pending";
    const anyEligible = [...dmEligibleByRoomId.values()].some(Boolean);
    if (anyEligible) return "ready";
    if ([...dmPendingByRoomId.values()].some(Boolean)) return "error";
    return "empty";
  }, [orgAll, orgRoster, dmEligibleByRoomId, dmPendingByRoomId]);

  const mutedRoomIds = useMemo(() => {
    if (!client) return new Set<string>();
    const out = new Set<string>();
    for (const room of rooms) {
      if (getRoomNotificationLevel(client, room.id) === "mute") out.add(room.id);
    }
    return out;
  }, [client, rooms]);

  const disambiguationByRoomId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const room of rooms) {
      if (room.isDirect) continue;
      const key = room.name.toLocaleLowerCase();
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    const out = new Map<string, string>();
    const ambiguous = rooms.filter(
      (room) => !room.isDirect && (counts.get(room.name.toLocaleLowerCase()) ?? 0) > 1,
    );
    if (ambiguous.length === 0) return out;
    for (const space of spaces) {
      const visibleIds = visibleRoomIdsForSpace(allRooms, space.id);
      for (const room of ambiguous) {
        if (!out.has(room.id) && visibleIds.has(room.id)) out.set(room.id, space.name);
      }
    }
    return out;
  }, [allRooms, rooms, spaces]);

  const unreadBySpace = useMemo(() => {
    const out = new Map<string, number>();
    for (const space of spaces) {
      const visibleIds = visibleRoomIdsForSpace(allRooms, space.id);
      let total = 0;
      for (const room of allRooms) {
        if (room.isSpace || room.isDirect || room.archived !== null) continue;
        if (!visibleIds.has(room.id)) continue;
        if (client && getRoomNotificationLevel(client, room.id) === "mute") continue;
        total += room.unreadTotal;
      }
      out.set(space.id, total);
    }
    return out;
  }, [allRooms, client, spaces]);

  return {
    spaces,
    rooms,
    mutedRoomIds,
    disambiguationByRoomId,
    unreadBySpace,
    dmStatus,
    retryDmRoster,
  };
}
